/**
 * Tenant Provider
 *
 * Syncs tenant context with auth context.
 * Automatically sets tenant from authenticated user's orgId.
 *
 * Part of the Identity domain - manages organization/tenant state.
 */

"use client";

import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  useTenant,
  TenantProvider as TenantContextProvider,
} from "../contexts/TenantContext";
import { useQueryClient } from "@tanstack/react-query";
import {
  setCurrentOrgId as setModuleOrgId,
  setAuthOrgId,
} from "@/services/shared/infrastructure/tenant-state";

/**
 * TenantProvider - Syncs tenant state with auth context
 *
 * This provider:
 * 1. Wraps TenantContextProvider
 * 2. Sets tenant from user's orgId when authenticated
 * 3. Clears tenant when user logs out
 * 4. Invalidates queries when tenant changes
 */
export function TenantProvider({ children }: { children: React.ReactNode }) {
  return (
    <TenantContextProvider>
      <TenantSync>{children}</TenantSync>
    </TenantContextProvider>
  );
}

function TenantSync({ children }: { children: React.ReactNode }) {
  const [isMounted, setIsMounted] = useState(false);
  const queryClient = useQueryClient();
  const { user, isPending: loading, isAuthenticated } = useAuth();
  const { currentOrgId, setTenant, clearTenant, setAvailableOrgs } =
    useTenant();

  // Mark as mounted after first render (client-side only)
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Sync tenant from user's orgIds (only on client)
  useEffect(() => {
    if (!isMounted || typeof window === "undefined") return;

    const primaryOrgId = user?.orgIds?.[0];
    // Update module-level state for api-client
    setAuthOrgId(primaryOrgId || null);

    if (isAuthenticated && primaryOrgId) {
      const previousOrgId = currentOrgId;

      // Set tenant if it changed
      if (primaryOrgId !== currentOrgId) {
        // Invalidate all queries for previous tenant if switching
        if (previousOrgId) {
          queryClient.removeQueries({
            predicate: (query) => {
              const key = query.queryKey;
              return (
                Array.isArray(key) &&
                key.includes("org") &&
                key.includes(previousOrgId)
              );
            },
          });
        }

        setTenant(primaryOrgId);
        // Update module-level state for api-client
        setModuleOrgId(primaryOrgId);

        // Set available orgs from user's orgIds array
        setAvailableOrgs(
          user.orgIds.map((orgId) => ({
            id: orgId,
            name: orgId.substring(0, 8),
          })),
        );
      }
    } else if (!isAuthenticated && !loading) {
      // Clear all tenant-scoped queries
      if (currentOrgId) {
        queryClient.removeQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && key.includes("org");
          },
        });
      }

      clearTenant();
      setModuleOrgId(null);
      setAvailableOrgs([]);
    }
  }, [
    isAuthenticated,
    user?.orgIds,
    loading,
    setTenant,
    clearTenant,
    setAvailableOrgs,
    currentOrgId,
    queryClient,
    isMounted,
  ]);

  return <>{children}</>;
}
